import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import { agencySessionKey, getAgencySession, setAgencySession, subscribeAgencySession } from "../../../utils/agencySession.ts";
import { AccountButton, AccountSelect } from "../../shared/account/AccountUI.tsx";
import PortalDialog from "../../shared/PortalDialog.tsx";
import AppFeedback from "../../shared/AppFeedback.tsx";
import type { AgencyChoice } from "./agencyIdentityService.ts";
import { readAgencyMemberships, selectAgencyMembership } from "./agencyMembershipService.ts";
import "../shared/StoreTextField.css";

// Existing multi-agency members switch membership in the panel, never at login.
// A company sales agent instead chooses the agency on each new order.
export default function AgencyMembershipSelector() {
  useStoreLanguage();
  const key = useSyncExternalStore(subscribeAgencySession, agencySessionKey);
  const session = getAgencySession();
  const eligible = session?.role === "Manager" || session?.role === "Staff";
  const [choices, setChoices] = useState<AgencyChoice[]>([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number>();
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  useEffect(function () {
    setChoices([]);
    setOpen(false);
    if (!eligible) return;
    let active = true;
    readAgencyMemberships().then(function (result) {
      if (active && key === agencySessionKey()) setChoices(result);
    }).catch(function (error) {
      if (active && key === agencySessionKey()) AppFeedback.httpError(error);
    });
    return function () { active = false; };
  }, [key, eligible]);
  if (!eligible || choices.length < 2) return null;

  async function select() {
    if (locked.current || !selected || key !== agencySessionKey()) return;
    locked.current = true;
    setBusy(true);
    try {
      await selectAgencyMembership(selected);
      setAgencySession(null);
      window.location.assign("/account/orders");
    } catch (error) {
      AppFeedback.httpError(error);
      locked.current = false;
      setBusy(false);
    }
  }

  return (
    <>
      <AccountButton
        disabled={busy}
        onClick={function () {
          setSelected(Number(session?.membershipId));
          setOpen(true);
        }}
      >
        {t("تغییر نماینده")}
      </AccountButton>
      <PortalDialog
        open={open}
        title={t("انتخاب نماینده")}
        busy={busy}
        onClose={function () { if (!busy) setOpen(false); }}
        footer={(
          <div className="account-ui__actions">
            <AccountButton
              type="primary"
              loading={busy}
              disabled={!selected || String(selected) === session?.membershipId}
              onClick={select}
            >
              {t("تأیید")}
            </AccountButton>
            <AccountButton
              disabled={busy}
              onClick={function () { setOpen(false); }}
            >
              {t("انصراف")}
            </AccountButton>
          </div>
        )}
      >
        <p>{t("با تغییر نماینده، صفحه دوباره باز می‌شود و تغییرات ذخیره‌نشده از بین می‌رود.")}</p>
        <div className="store-text-field">
          <label htmlFor="agency-membership-choice">{t("نمایندگی")}</label>
          <AccountSelect
            id="agency-membership-choice"
            showSearch
            optionFilterProp="label"
            value={selected}
            disabled={busy}
            getPopupContainer={function (trigger) { return trigger.parentElement!; }}
            options={choices.map(function (choice) {
              return { value: choice.membershipId, label: choice.title };
            })}
            onChange={setSelected}
          />
        </div>
      </PortalDialog>
    </>
  );
}
